import { useEffect, useState } from "react";
import { api } from "../api/client.js";
import { getAdminToken } from "./auth.js";

const toDraft = (settings) => ({
  phoneNumbers: (settings.phoneNumbers ?? []).join("\n"),
  emailAddresses: (settings.emailAddresses ?? []).join("\n"),
  businessAddress: settings.businessAddress ?? "",
  facebook: settings.socialLinks?.facebook ?? "",
  instagram: settings.socialLinks?.instagram ?? "",
  pinterest: settings.socialLinks?.pinterest ?? "",
  youtube: settings.socialLinks?.youtube ?? "",
});

const splitLines = (value) =>
  value
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);

export default function SiteSettingsManager({
  settings,
  onChanged,
  onUnauthorized,
}) {
  const [draft, setDraft] = useState(() => toDraft(settings));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => setDraft(toDraft(settings)), [settings]);

  const handleChange = ({ target: { name, value } }) => {
    setDraft((current) => ({ ...current, [name]: value }));
    setError("");
    setNotice("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      await api.admin.siteSettings.update(
        {
          phoneNumbers: splitLines(draft.phoneNumbers),
          emailAddresses: splitLines(draft.emailAddresses),
          businessAddress: draft.businessAddress.trim(),
          socialLinks: {
            facebook: draft.facebook.trim(),
            instagram: draft.instagram.trim(),
            pinterest: draft.pinterest.trim(),
            youtube: draft.youtube.trim(),
          },
        },
        getAdminToken(),
      );
      setNotice("Public contact information updated.");
      await onChanged();
    } catch (requestError) {
      if (requestError.status === 401 || requestError.status === 403) {
        onUnauthorized();
        return;
      }
      const validationMessage = requestError.details
        ?.map((detail) => detail.message)
        .join(" ");
      setError(
        validationMessage ||
          requestError.message ||
          "Could not update site settings",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="admin-panel admin-settings-panel">
      <div className="admin-panel-heading">
        <div>
          <p className="admin-eyebrow">Public website</p>
          <h2>Contact and social settings</h2>
        </div>
      </div>

      <form className="admin-settings-form" onSubmit={handleSubmit}>
        <div className="admin-form-grid">
          <label>
            <span>Email addresses</span>
            <textarea
              name="emailAddresses"
              value={draft.emailAddresses}
              onChange={handleChange}
              placeholder="One email address per line"
            />
          </label>
          <label>
            <span>Phone numbers</span>
            <textarea
              name="phoneNumbers"
              value={draft.phoneNumbers}
              onChange={handleChange}
              placeholder="One phone number per line"
            />
          </label>
        </div>

        <label>
          <span>Business address</span>
          <textarea
            name="businessAddress"
            value={draft.businessAddress}
            onChange={handleChange}
            placeholder="Street, building, city, country"
          />
        </label>

        <div className="admin-form-grid">
          {["facebook", "instagram", "pinterest", "youtube"].map((network) => (
            <label key={network}>
              <span>
                {network[0].toUpperCase()}
                {network.slice(1)} URL
              </span>
              <input
                type="url"
                name={network}
                value={draft[network]}
                onChange={handleChange}
                placeholder={`https://${network}.com/...`}
              />
            </label>
          ))}
        </div>

        {error && (
          <p className="admin-form-error" role="alert">
            {error}
          </p>
        )}
        {notice && (
          <p className="admin-form-notice" role="status">
            {notice}
          </p>
        )}

        <div className="admin-form-actions">
          <button type="submit" disabled={submitting}>
            {submitting ? "Saving…" : "Save public settings"}
          </button>
        </div>
      </form>
    </section>
  );
}
